import {
  Page,
  Layout,
  Card,
  Text,
  Select,
  Banner,
  SkeletonBodyText,
} from '@shopify/polaris'
import { LineChart, DonutChart } from '@shopify/polaris-viz'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { useShopifyAuth } from '../hooks/useShopifyAuth'

// Generate date labels for the last N days
function getDateLabels(days: number): string[] {
  const labels: string[] = []
  const today = new Date()
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)
    labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))
  }
  return labels
}

export default function Analytics() {
  useShopifyAuth()

  const [period, setPeriod] = useState('30d')
  const isPreviewMode = !window.shopify

  const mockData = {
    revenue_attributed: 12450,
    discount_savings: 1867,
    messages_sent: 847,
    conversion_rate: 23.4,
    cost_per_message: 0.023,
    daily_revenue: [320, 450, 280, 590, 420, 680, 520, 410, 630, 550, 480, 720, 610, 530, 490, 580, 670, 540, 620, 710, 590, 480, 650, 720, 580, 690, 750, 620, 810, 890],
  }

  const { data: apiData, isLoading, error } = useQuery({
    queryKey: ['analytics', period],
    queryFn: () => api.getAnalytics(period),
    enabled: !isPreviewMode,
  })

  const data = isPreviewMode ? mockData : apiData
  const conversions = Math.round((data?.messages_sent || 0) * (data?.conversion_rate || 0) / 100)

  const periodOptions = [
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 30 days', value: '30d' },
    { label: 'Last 90 days', value: '90d' },
  ]

  // Prepare data for Polaris Viz LineChart
  const dailyRevenue = data?.daily_revenue || mockData.daily_revenue
  const dateLabels = getDateLabels(dailyRevenue.length)
  const lineChartData = [{
    name: 'Revenue',
    data: dailyRevenue.map((value, index) => ({
      key: dateLabels[index],
      value: value,
    })),
  }]

  // Prepare data for Polaris Viz DonutChart
  // Use actual counts (not percentages) so the center shows real numbers
  const hasConversions = conversions > 0
  const hasMessages = (data?.messages_sent || 0) > 0
  const donutChartData = hasConversions ? [
    {
      name: 'Converted',
      data: [{ key: 'Converted', value: conversions }],
    },
    {
      name: 'Not converted',
      data: [{ key: 'Not converted', value: (data?.messages_sent || 0) - conversions }],
      color: '#e4e5e7',
    },
  ] : []  // Empty: we show placeholder instead

  if (error && !isPreviewMode) {
    return (
      <Page title="Analytics">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load analytics. Please refresh the page.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page title="Analytics">
      <Layout>
        {/* Period Selector */}
        <Layout.Section>
          <div style={{ display: 'inline-block' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              border: '1px solid #c9cccf',
              borderRadius: '8px',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M5 4.5A1.5 1.5 0 016.5 3H7V2.5a.5.5 0 011 0V3h4v-.5a.5.5 0 011 0V3h.5A1.5 1.5 0 0115 4.5v11a1.5 1.5 0 01-1.5 1.5h-7A1.5 1.5 0 015 15.5v-11z" fill="#5c5f62"/>
              </svg>
              <Select
                label=""
                labelHidden
                options={periodOptions}
                value={period}
                onChange={setPeriod}
              />
            </div>
          </div>
        </Layout.Section>

        {/* Stats Row */}
        <Layout.Section>
          {isLoading ? (
            <Card><SkeletonBodyText lines={2} /></Card>
          ) : (
            <div style={{ display: 'flex', border: '1px solid #e1e3e5', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}>
              <div style={{ flex: 1, padding: '16px 20px', borderRight: '1px solid #e1e3e5' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <Text as="p" variant="bodySm" fontWeight="medium">Added revenue</Text>
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1px solid #babec3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#6d7175' }}>i</span>
                </div>
                <div style={{ fontSize: '24px', fontWeight: 400, color: '#202223' }}>${(data?.revenue_attributed || 0).toLocaleString()}</div>
              </div>
              <div style={{ flex: 1, padding: '16px 20px', borderRight: '1px solid #e1e3e5' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <Text as="p" variant="bodySm" fontWeight="medium">Discount savings</Text>
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1px solid #babec3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#6d7175' }}>i</span>
                </div>
                <div style={{ fontSize: '24px', fontWeight: 400, color: '#202223' }}><span style={{ color: '#008060' }}>+</span>${(data?.discount_savings || 0).toLocaleString()}</div>
              </div>
              <div style={{ flex: 1, padding: '16px 20px', borderRight: '1px solid #e1e3e5' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <Text as="p" variant="bodySm" fontWeight="medium">Recovered checkouts</Text>
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1px solid #babec3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#6d7175' }}>i</span>
                </div>
                <div style={{ fontSize: '24px', fontWeight: 400, color: '#202223' }}>{conversions}</div>
              </div>
              <div style={{ flex: 1, padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <Text as="p" variant="bodySm" fontWeight="medium">Cost per message</Text>
                  <span style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1px solid #babec3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#6d7175' }}>i</span>
                </div>
                <div style={{ fontSize: '24px', fontWeight: 400, color: '#202223' }}>${(data?.cost_per_message || 0).toFixed(2)}</div>
              </div>
            </div>
          )}
        </Layout.Section>

        {/* Charts Row */}
        <Layout.Section>
          {isLoading ? (
            <Card><SkeletonBodyText lines={8} /></Card>
          ) : (
            <div style={{ display: 'flex', gap: '16px' }}>
              {/* Conversion Chart - ~35% width */}
              <div style={{
                flex: '0 0 35%',
                padding: '20px 24px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e1e3e5',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '400px',
              }}>
                <Text as="h2" variant="headingMd" fontWeight="semibold">
                  Conversion rate
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  See how many customers are converting from SMS recovery.
                </Text>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '280px' }}>
                  {hasConversions ? (
                    <DonutChart
                      data={donutChartData}
                      legendPosition="bottom"
                      showLegend={false}
                    />
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ width: '140px', height: '140px', borderRadius: '50%', border: '12px solid #e4e5e7', margin: '0 auto 12px' }} />
                      <Text as="p" variant="bodySm" tone="subdued">
                        {hasMessages ? 'No conversions yet' : 'No messages sent yet'}
                      </Text>
                    </div>
                  )}
                </div>
                <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                  {hasMessages ? `${conversions} of ${data?.messages_sent || 0} converted` : '\u00A0'}
                </Text>
              </div>

              {/* Revenue Chart - ~65% width */}
              <div style={{
                flex: '1',
                padding: '20px 24px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e1e3e5',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '400px',
              }}>
                <Text as="h2" variant="headingMd" fontWeight="semibold">
                  Daily added revenue
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  See how much additional revenue you're making with this app every day.
                </Text>
                <div style={{ flex: 1, minHeight: '280px', marginTop: '16px' }}>
                  <LineChart
                    data={lineChartData}
                    showLegend={false}
                    xAxisOptions={{
                      labelFormatter: (value) => String(value),
                    }}
                    yAxisOptions={{
                      labelFormatter: (value) => `$${Number(value).toLocaleString()}`,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  )
}
